import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Input } from './FormField';
import { Button } from '../ui/Button';
import { ErrorDisplay } from '../ui/ErrorDisplay';
import { ColorPicker } from './ColorPicker';
import { classificationApi } from '../../services/api';

interface Classification {
  id: string;
  name: string;
  badgeLabel: string;
  color: string;
}

interface ClassificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  classification?: Classification | null;
  onSuccess: () => void;
}

export function ClassificationModal({
  isOpen,
  onClose,
  classification,
  onSuccess,
}: ClassificationModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    badgeLabel: '',
    color: '#3B82F6',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(classification);

  // Populate form when editing
  useEffect(() => {
    if (classification) {
      setFormData({
        name: classification.name || '',
        badgeLabel: classification.badgeLabel || '',
        color: classification.color || '#3B82F6',
      });
    } else {
      setFormData({
        name: '',
        badgeLabel: '',
        color: '#3B82F6',
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [classification, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Name must be 50 characters or less';
    }

    if (!formData.badgeLabel.trim()) {
      newErrors.badgeLabel = 'Badge label is required';
    } else if (formData.badgeLabel.trim().length > 20) {
      newErrors.badgeLabel = 'Badge label must be 20 characters or less';
    }

    if (!formData.color || !/^#[0-9A-F]{6}$/i.test(formData.color)) {
      newErrors.color = 'Valid color is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const data = {
        name: formData.name.trim(),
        badgeLabel: formData.badgeLabel.trim(),
        color: formData.color,
      };

      if (isEditMode && classification) {
        await classificationApi.update(classification.id, data);
      } else {
        await classificationApi.create(data);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save classification:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to save classification'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!classification) return;

    if (!confirm(`Are you sure you want to delete "${classification.name}"?`)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await classificationApi.delete(classification.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to delete classification:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to delete classification'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit classification' : 'Add new classification'}
      size="small"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="New feature"
          required
          error={errors.name}
        />

        <Input
          label="Badge label"
          name="badgeLabel"
          value={formData.badgeLabel}
          onChange={(e) => setFormData({ ...formData, badgeLabel: e.target.value })}
          placeholder="NEW"
          required
          error={errors.badgeLabel}
          helpText="Short label for timeline legend (e.g. BAU, Audit, New Feature)"
          maxLength={20}
        />

        <ColorPicker
          label="Color"
          value={formData.color}
          onChange={(color) => setFormData({ ...formData, color })}
          required
          error={errors.color}
        />

        <div className="rounded-md bg-surface-subtle border border-border-subtle p-3">
          <div className="text-sm font-medium text-text-primary mb-2">
            Legend preview
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: formData.color }}
            />
            <span className="text-xs font-medium text-text-secondary">
              {formData.badgeLabel.trim() || 'LABEL'}
            </span>
          </div>
        </div>

        <ErrorDisplay error={submitError} />

        <ModalFooter>
          {isEditMode && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="mr-auto"
            >
              Delete
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Save changes' : 'Add classification'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
